import { Textarea } from '@ibsys/kit';
import { useSortable } from '@dnd-kit/react/sortable';
import { GripVerticalIcon } from 'lucide-react';
import { FC, useCallback, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useGenerateTitle } from '@/entities/content';
import { hasPredicateIssues } from '@/entities/predicate';
import { useHasPermission } from '@/entities/profile';
import { PredicateInput } from '@/features/PredicateInput';
import { cn, NAVIGATION_TARGET_HIGHLIGHT_CLASS, type NavigationTarget } from '@/shared/lib';
import { ConfirmationButton, EditableTitle } from '@/shared/ui';
import { KmEvalNoteSets } from './KmEvalNoteSets';
import type { KmAssessmentFormValues } from './types';

type KmEvalRowProps = {
  index: number;
  sortableId: string;
  /** Порядковый номер элемента в списке сортировки */
  sortableIndex: number;
  onRemove: () => void;
  isUpdating: boolean;
  /** Наведение на определение, к которому ведёт переход из результата заключения */
  evalTarget: NavigationTarget;
};

/**
 * Определение оценки внутри единицы оценивания: название, условие применимости, текст и наборы
 * знаний, на которые оно опирается. Определение, условие которого диалогу не подходит, в его
 * оценивание не берётся.
 */
export const KmEvalRow: FC<KmEvalRowProps> = ({
  index,
  sortableId,
  sortableIndex,
  onRemove,
  isUpdating,
  evalTarget,
}) => {
  const { t } = useTranslation(['common', 'expertReport']);
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  const { control, getValues, setValue } = useFormContext<KmAssessmentFormValues>();
  const { mutate: generateTitle } = useGenerateTitle();
  const nameGeneratedRef = useRef(false);

  const name = `evals.${index}` as const;
  const evalName = useWatch({ control, name: `${name}.name` });
  const evalId = useWatch({ control, name: `${name}.id` });
  // Определение добавлено в форме и ещё не сохранено — своего номера у него нет, целью перехода
  // оно быть не может
  const isTarget = evalId !== undefined && evalTarget.targetKey === String(evalId);

  const handleTextBlur = useCallback(() => {
    if (nameGeneratedRef.current) return;

    const item = getValues(`evals.${index}`);
    if (!item) return;

    const trimmedText = item.text.trim();
    const wordCount = trimmedText.split(/\s+/).filter((word) => word.length > 0).length;

    if (wordCount >= 5 && !item.name.trim()) {
      nameGeneratedRef.current = true;
      generateTitle(
        { text: trimmedText, entityType: 'expertReportRule' },
        {
          onSuccess: (response) => {
            setValue(`evals.${index}.name`, response.title, { shouldDirty: true });
          },
          onError: () => {
            nameGeneratedRef.current = false;
          },
        },
      );
    }
  }, [index, getValues, setValue, generateTitle]);

  const handleNameSave = useCallback(
    (newName: string) => {
      setValue(`evals.${index}.name`, newName, { shouldDirty: true });
    },
    [index, setValue],
  );

  const { ref, handleRef, isDragging } = useSortable({
    id: sortableId,
    index: sortableIndex,
    disabled: !allowAdmin,
  });

  return (
    <div
      ref={(element) => {
        ref(element);
        if (isTarget) evalTarget.attachTarget(element);
      }}
      className={cn(
        '@container/km-eval-row group/km-eval relative rounded-lg border border-border p-3 pl-5 space-y-3',
        isDragging && 'opacity-50',
        isTarget && NAVIGATION_TARGET_HIGHLIGHT_CLASS,
      )}
    >
      {allowAdmin && (
        <div
          className="absolute left-0.5 top-3 cursor-grab text-muted-foreground opacity-0 transition-opacity group-hover/km-eval:opacity-100"
          ref={(element) => handleRef(element)}
        >
          <GripVerticalIcon size={16} />
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <EditableTitle
            value={evalName}
            label={t('expertReport:km.renameEval')}
            placeholder={t('expertReport:km.evalName')}
            onSave={handleNameSave}
            textClassName="text-sm font-medium truncate"
            disabled={!allowAdmin}
          />
        </div>
        {allowAdmin && (
          <ConfirmationButton
            onConfirm={onRemove}
            isLoadingConfirm={isUpdating}
            title={t('expertReport:km.removeEval')}
            description={
              <Trans
                i18nKey="expertReport:km.removeEvalDescription"
                values={{ evalName }}
                components={{ strong: <strong /> }}
              />
            }
            trigger={{
              variant: 'ghost',
              color: 'destructive',
              size: 'sm',
              children: t('expertReport:km.deleteEval'),
            }}
          />
        )}
      </div>

      <PredicateInput
        control={control}
        name={`${name}.predicate`}
        type="textarea"
        label={t('expertReport:predicate')}
        classNames={{ input: 'field-sizing-content' }}
        disabled={!allowAdmin}
        rules={{
          validate: (value) => !hasPredicateIssues(value) || t('common:predicateHasIssues'),
        }}
      />

      <Textarea
        control={control}
        name={`${name}.text`}
        label={t('expertReport:km.evalText')}
        onBlur={handleTextBlur}
        classNames={{ input: 'resize-none field-sizing-content' }}
        disabled={!allowAdmin}
      />

      <KmEvalNoteSets index={index} />
    </div>
  );
};
